import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { FileText, Search, Send, Receipt, Plus, RefreshCw, Trash2, Download, Pencil, Copy } from "lucide-react";
import { downloadInvoicePdf } from "@/lib/invoice-pdf";
import RecordEditDialog from "@/components/admin/RecordEditDialog";
import CreateInvoiceSheet from "@/components/admin/CreateInvoiceSheet";
import { invoiceFields, invoiceCreateFields } from "@/components/admin/recordFields";

type InvoiceStatus = "draft" | "open" | "paid" | "uncollectible" | "void";

interface InvoiceRow {
  id: string;
  order_id: string | null;
  user_id: string;
  subscription_id: string | null;
  stripe_invoice_id: string | null;
  status: InvoiceStatus | string;
  amount_cents: number | null;
  currency: string | null;
  due_date: string | null;
  hosted_invoice_url: string | null;
  pdf_url: string | null;
  number: string | null;
  sent_at: string | null;
  paid_at: string | null;
  manually_marked_paid: boolean;
  created_at: string;
  ocr_reference: string | null;
  reminder_count: number | null;
  reminder_1_sent_at: string | null;
  reminder_2_sent_at: string | null;
  reminder_3_sent_at: string | null;
  stripe_status: string | null;
  billing_reason: string | null;
  collection_method: string | null;
  amount_remaining_cents: number | null;
  attempt_count: number | null;
  last_payment_error: string | null;
  period_start: string | null;
  period_end: string | null;
  last_synced_at: string | null;
  updated_at?: string | null;
}

// The saved Stripe status is only worth showing when it is at least as fresh
// as our own last change. Otherwise it is a leftover from before we voided /
// paid the invoice and would contradict the real status.
const showStripeStatus = (inv: {
  status: string;
  stripe_status: string | null;
  last_synced_at: string | null;
  updated_at?: string | null;
}) => {
  if (!inv.stripe_status || inv.stripe_status === inv.status) return false;
  if (!inv.last_synced_at) return false;
  if (inv.updated_at && new Date(inv.last_synced_at) < new Date(inv.updated_at)) return false;
  return true;
};


interface ProfileMini {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
}

interface OrderMini {
  id: string;
  product_name: string;
  order_mode: string | null;
  payment_status: string | null;
  total_cents: number | null;
  currency: string | null;
  created_at: string;
  user_id: string;
  stripe_invoice_id: string | null;
}

const formatSEK = (cents: number | null | undefined, currency = "SEK") => {
  if (cents == null) return "—";
  return new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
};
const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric" }) : "—";

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    draft: "bg-muted text-muted-foreground hover:bg-muted",
    open: "bg-amber-100 text-amber-900 hover:bg-amber-100",
    paid: "bg-emerald-100 text-emerald-900 hover:bg-emerald-100",
    uncollectible: "bg-rose-100 text-rose-900 hover:bg-rose-100",
    void: "bg-muted text-muted-foreground hover:bg-muted",
  };
  const labels: Record<string, string> = {
    draft: "Utkast",
    open: "Att betala",
    paid: "Betald",
    uncollectible: "Förfallen",
    void: "Makulerad",
  };
  return (
    <Badge className={map[status] ?? "bg-muted text-muted-foreground hover:bg-muted"}>
      {labels[status] ?? status}
    </Badge>
  );

};

const billingReasonLabel = (reason: string | null) => {
  if (!reason) return "";
  const map: Record<string, string> = {
    subscription_create: "Första månaden",
    subscription_cycle: "Månadsförnyelse",
    subscription_update: "Ändrat abonnemang",
    subscription_threshold: "Abonnemang",
    manual: "Manuell faktura",
    upcoming: "Kommande",
  };
  return map[reason] ?? reason;
};

const periodLabel = (start: string | null, end: string | null) => {
  if (!start) return "";
  const fmt = (d: string) =>
    new Date(`${d}T00:00:00`).toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
  return end ? `${fmt(start)}–${fmt(end)}` : fmt(start);
};


const isOverdue = (inv: InvoiceRow) => {
  if (!inv.due_date) return false;
  if (inv.status === "paid" || inv.status === "void") return false;
  return new Date(inv.due_date) < new Date();
};

const AdminInvoicesPage = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileMini[]>([]);
  const [subNicknames, setSubNicknames] = useState<Record<string, string>>({});
  const [pausedByInvoice, setPausedByInvoice] = useState<Record<string, string>>({});
  const [unbilledOrders, setUnbilledOrders] = useState<OrderMini[]>([]);
  const [tab, setTab] = useState<"all" | "draft" | "open" | "paid" | "overdue">("all");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "subscription" | "onetime">("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncFailed, setSyncFailed] = useState(false);
  const [confirmPayId, setConfirmPayId] = useState<string | null>(null);
  const [confirmVoidId, setConfirmVoidId] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [resendVoidId, setResendVoidId] = useState<string | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showCreateFor, setShowCreateFor] = useState<OrderMini | null>(null);
  const [editInvoice, setEditInvoice] = useState<Record<string, unknown> | null>(null);
  const [manualInvoiceOpen, setManualInvoiceOpen] = useState(false);
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
  const [copySource, setCopySource] = useState<{ id: string; number: string | null } | null>(null);
  const [searchParams] = useSearchParams();
  const focusOrderId = searchParams.get("focus");
  const focusRowRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (!loading && focusOrderId && focusRowRef.current) {
      focusRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, focusOrderId, unbilledOrders]);

  const load = async () => {
    setLoading(true);
    const [invRes, profRes, ordersRes] = await Promise.all([
      supabase.from("invoices").select("*").order("created_at", { ascending: false }),
      supabase.from("profiles").select("id, email, first_name, last_name, company"),
      supabase
        .from("orders")
        .select("id, product_name, order_mode, payment_status, total_cents, currency, created_at, user_id, stripe_invoice_id, payment_method")
        .is("stripe_invoice_id", null)
        .not("payment_status", "in", "(paid,scheduled,cancelled,paused)")
        .neq("payment_method", "card")
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    if (invRes.error) toast.error("Kunde inte ladda fakturor");
    if (profRes.error) toast.error("Kunde inte ladda kunder");
    const invs = (invRes.data ?? []) as InvoiceRow[];
    setInvoices(invs);
    setProfiles(profRes.data ?? []);
    setUnbilledOrders((ordersRes.data ?? []) as OrderMini[]);

    const subIds = Array.from(new Set(invs.map((i) => i.subscription_id).filter(Boolean) as string[]));
    if (subIds.length > 0) {
      const { data: ss } = await supabase
        .from("subscriptions")
        .select("id, nickname")
        .in("id", subIds);
      const m: Record<string, string> = {};
      ((ss as Array<{ id: string; nickname: string | null }>) || []).forEach((s) => {
        if (s.nickname) m[s.id] = s.nickname;
      });
      setSubNicknames(m);
    }

    const { data: pausedSubs } = await supabase
      .from("subscriptions")
      .select("id, paused_for_overdue_invoice_id")
      .eq("paused_for_overdue", true);
    const pm: Record<string, string> = {};
    ((pausedSubs as Array<{ id: string; paused_for_overdue_invoice_id: string | null }>) || []).forEach((s) => {
      if (s.paused_for_overdue_invoice_id) pm[s.paused_for_overdue_invoice_id] = s.id;
    });
    setPausedByInvoice(pm);

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  // Live status from Stripe: refresh every invoice that has a Stripe link,
  // plus a broad sweep that picks up invoices Stripe created on its own.
  const syncFromStripe = async (opts: { silent?: boolean } = {}) => {
    setSyncing(true);
    try {
      const { data: rows } = await supabase
        .from("invoices")
        .select("id")
        .not("stripe_invoice_id", "is", null)
        .order("created_at", { ascending: false })
        .limit(300);
      const ids = (rows ?? []).map((r) => r.id as string);

      const calls: Promise<unknown>[] = [
        supabase.functions.invoke("admin-sync-invoices-from-stripe", {
          body: { all_active: true },
        }),
      ];
      if (ids.length > 0) {
        calls.push(
          supabase.functions.invoke("admin-sync-invoices-from-stripe", {
            body: { invoice_ids: ids },
          }),
        );
      }
      await Promise.all(calls);
      await load();
      setLastSyncedAt(new Date());
      setSyncFailed(false);
      if (!opts.silent) toast.success("Uppdaterad från Stripe");
    } catch (e: any) {
      setSyncFailed(true);
      if (!opts.silent) toast.error(e?.message ?? "Kunde inte hämta från Stripe");
    } finally {
      setSyncing(false);
    }
  };

  const autoSyncedRef = useRef(false);
  useEffect(() => {
    if (autoSyncedRef.current) return;
    autoSyncedRef.current = true;
    syncFromStripe({ silent: true });
  }, []);

  const syncOneInvoice = async (invoiceId: string) => {
    setBusyId(invoiceId);
    try {
      const { data, error } = await supabase.functions.invoke("admin-sync-invoices-from-stripe", {
        body: { invoice_id: invoiceId },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      const n = (data as any)?.synced ?? 0;
      toast.success(`Synkad — ${n} version${n === 1 ? "" : "er"} hittade`);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Kunde inte synka fakturan");
    } finally {
      setBusyId(null);
    }
  };

  const profileMap = useMemo(() => {
    const m = new Map<string, ProfileMini>();
    profiles.forEach((p) => m.set(p.id, p));
    return m;
  }, [profiles]);

  const isSubscriptionInvoice = (inv: InvoiceRow) =>
    Boolean(inv.subscription_id) ||
    inv.billing_reason === "subscription_create" ||
    inv.billing_reason === "subscription_cycle" ||
    inv.billing_reason === "subscription_update";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (tab === "overdue" && !isOverdue(inv)) return false;
      if (tab !== "all" && tab !== "overdue" && inv.status !== tab) return false;
      if (typeFilter === "subscription" && !isSubscriptionInvoice(inv)) return false;
      if (typeFilter === "onetime" && isSubscriptionInvoice(inv)) return false;
      if (!q) return true;
      const p = profileMap.get(inv.user_id);
      const haystack = [
        inv.number,
        inv.stripe_invoice_id,
        inv.ocr_reference,
        p?.email,
        p?.first_name,
        p?.last_name,
        p?.company,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [invoices, tab, typeFilter, search, profileMap]);


  const counts = useMemo(() => {
    return {
      all: invoices.length,
      draft: invoices.filter((i) => i.status === "draft").length,
      open: invoices.filter((i) => i.status === "open").length,
      paid: invoices.filter((i) => i.status === "paid").length,
      overdue: invoices.filter(isOverdue).length,
    };
  }, [invoices]);

  const totals = useMemo(() => {
    const outstanding = invoices
      .filter((i) => i.status === "open")
      .reduce((acc, i) => acc + (i.amount_cents ?? 0), 0);
    const paidThisMonth = (() => {
      const now = new Date();
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
      return invoices
        .filter((i) => i.status === "paid" && i.paid_at && i.paid_at >= start)
        .reduce((acc, i) => acc + (i.amount_cents ?? 0), 0);
    })();
    const overdueAmount = invoices.filter(isOverdue).reduce((acc, i) => acc + (i.amount_cents ?? 0), 0);
    return { outstanding, paidThisMonth, overdueAmount };
  }, [invoices]);

  const createInvoice = async (orderId: string, send: boolean) => {
    setBusyId(orderId);
    try {
      const { data, error } = await supabase.functions.invoke("admin-create-and-send-invoice", {
        body: { order_id: orderId, send, days_until_due: 30 },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(send ? "Faktura skapad och skickad" : "Faktura skapad som utkast");
      setShowCreateFor(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Kunde inte skapa faktura");
    } finally {
      setBusyId(null);
    }
  };

  const sendDraft = async (invoice: InvoiceRow) => {
    if (!invoice.order_id) {
      toast.error("Ingen kopplad order — kan inte skicka via funktionen");
      return;
    }
    setBusyId(invoice.id);
    try {
      const { data, error } = await supabase.functions.invoke("admin-create-and-send-invoice", {
        body: { order_id: invoice.order_id, send: true, days_until_due: 30 },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success("Faktura skickad");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Kunde inte skicka faktura");
    } finally {
      setBusyId(null);
    }
  };

  const markPaid = async (invoiceId: string) => {
    setBusyId(invoiceId);
    setConfirmPayId(null);
    try {
      const { data, error } = await supabase.functions.invoke("admin-mark-invoice-paid", {
        body: { invoice_id: invoiceId },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success("Markerad som betald");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Kunde inte markera som betald");
    } finally {
      setBusyId(null);
    }
  };

  const resendVoidNotice = async (invoiceId: string) => {
    setBusyId(invoiceId);
    setResendVoidId(null);
    const reason = voidReason.trim();
    setVoidReason("");
    try {
      const { data, error } = await supabase.functions.invoke("admin-void-invoice", {
        body: {
          invoice_id: invoiceId,
          mode: "resend_notification",
          reason: reason || undefined,
        },
      });
      if (error) {
        let detail = error.message;
        try {
          const ctx = (error as any)?.context;
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json();
            if (body?.error) detail = body.error;
          }
        } catch {
          /* keep generic message */
        }
        throw new Error(detail);
      }
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success("Makuleringsmejl skickat till kunden");
    } catch (e: any) {
      toast.error(e?.message ?? "Kunde inte skicka mejlet");
    } finally {
      setBusyId(null);
    }
  };

  const voidInvoice = async (invoiceId: string) => {
    setBusyId(invoiceId);
    setConfirmVoidId(null);
    const reason = voidReason.trim();
    setVoidReason("");
    try {
      const { data, error } = await supabase.functions.invoke("admin-void-invoice", {
        body: { invoice_id: invoiceId, reason: reason || undefined },
      });
      if (error) {
        let detail = error.message;
        try {
          const ctx = (error as any)?.context;
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json();
            if (body?.error) detail = body.error;
          }
        } catch {
          /* keep generic message */
        }
        throw new Error(detail);
      }
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(
        (data as any)?.emailed
          ? "Fakturan makulerad – kunden har fått ett mejl"
          : "Fakturan makulerad",
      );
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Kunde inte makulera fakturan");
    } finally {
      setBusyId(null);
    }
  };


  const deleteInvoice = async (invoiceId: string) => {
    setBusyId(invoiceId);
    setConfirmDeleteId(null);
    try {
      const { error } = await supabase.from("invoices").delete().eq("id", invoiceId);
      if (error) throw error;
      toast.success("Faktura borttagen från admin");
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "Kunde inte ta bort fakturan");
    } finally {
      setBusyId(null);
    }
  };

  const customerName = (uid: string) => {
    const p = profileMap.get(uid);
    if (!p) return "Okänd kund";
    return [p.first_name, p.last_name].filter(Boolean).join(" ") || p.company || p.email;
  };

  return (
    <AdminLayout title="Fakturor">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="max-w-xl space-y-1">
            <p className="text-sm text-muted-foreground">
              Fakturor skapas automatiskt när du markerar en leverans som skickad. Du kan även skapa en
              faktura manuellt för en order som inte betalats vid checkout.
            </p>
            <p className="text-xs text-muted-foreground">
              {syncing
                ? "Hämtar aktuell status från Stripe…"
                : syncFailed
                  ? "Kunde inte nå Stripe — visar senast kända status."
                  : lastSyncedAt
                    ? `Uppdaterad från Stripe ${lastSyncedAt.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}`
                    : "Statusen speglar Stripe."}
            </p>
          </div>
          <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setCreateInvoiceOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-2" />
            Ny faktura
          </Button>
          <Button size="sm" variant="outline" onClick={() => setManualInvoiceOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-2" />
            Lägg till manuellt
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={syncing}
            onClick={() => syncFromStripe()}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${syncing ? "animate-spin" : ""}`} />
            Uppdatera från Stripe
          </Button>
          </div>
        </div>


        {/* KPI */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Utestående</p>
              {loading ? <Skeleton className="h-8 w-24 mt-1" /> : (
                <p className="text-2xl font-semibold mt-1">{formatSEK(totals.outstanding)}</p>
              )}
              <p className="text-[11px] text-muted-foreground mt-1">Skickat, ej betalt</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Förfallna</p>
              {loading ? <Skeleton className="h-8 w-24 mt-1" /> : (
                <p className="text-2xl font-semibold mt-1 text-rose-700">{formatSEK(totals.overdueAmount)}</p>
              )}
              <p className="text-[11px] text-muted-foreground mt-1">{counts.overdue} faktura(or)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Betalt denna månad</p>
              {loading ? <Skeleton className="h-8 w-24 mt-1" /> : (
                <p className="text-2xl font-semibold mt-1">{formatSEK(totals.paidThisMonth)}</p>
              )}
              <p className="text-[11px] text-muted-foreground mt-1">Summa registrerade betalningar</p>
            </CardContent>
          </Card>
        </div>

        {/* Unbilled orders */}
        {unbilledOrders.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Ofakturerade ordrar ({unbilledOrders.length})
                </h2>
                <p className="text-xs text-muted-foreground">Skapa faktura manuellt</p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kund</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Skapad</TableHead>
                    <TableHead className="text-right">Belopp</TableHead>
                    <TableHead className="text-right">Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...unbilledOrders]
                    .sort((a, b) => (a.id === focusOrderId ? -1 : b.id === focusOrderId ? 1 : 0))
                    .slice(0, 8)
                    .map((o) => {
                    const isFocused = o.id === focusOrderId;
                    const isAwaiting = o.payment_status === "awaiting_payment";
                    return (
                    <TableRow
                      key={o.id}
                      ref={isFocused ? focusRowRef : undefined}
                      className={isFocused ? "ring-2 ring-primary/60 bg-primary/5 transition" : ""}
                    >
                      <TableCell>
                        <Link to={`/admin/customers/${o.user_id}`} className="font-medium hover:underline">
                          {customerName(o.user_id)}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div>{o.product_name}</div>
                        {isAwaiting && (
                          <Badge className="mt-1 bg-blue-100 text-blue-900 hover:bg-blue-100 text-[10px] font-normal">
                            Väntar på kundbetalning
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(o.created_at)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {formatSEK(o.total_cents, o.currency || "SEK")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant={isFocused ? "default" : "outline"}
                          disabled={busyId === o.id}
                          onClick={() => setShowCreateFor(o)}
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Skapa faktura
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Filter + search */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList>
              <TabsTrigger value="all">Alla ({counts.all})</TabsTrigger>
              <TabsTrigger value="draft">Utkast ({counts.draft})</TabsTrigger>
              <TabsTrigger value="open">Öppna ({counts.open})</TabsTrigger>
              <TabsTrigger value="overdue">Förfallna ({counts.overdue})</TabsTrigger>
              <TabsTrigger value="paid">Betalda ({counts.paid})</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-3">
            <Tabs value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}>
              <TabsList>
                <TabsTrigger value="all">Alla typer</TabsTrigger>
                <TabsTrigger value="subscription">Abonnemang</TabsTrigger>
                <TabsTrigger value="onetime">Engångsköp</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-72">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Sök kund, fakturanummer, OCR..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </div>


        {/* Invoice table */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Inga fakturor matchar.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nr</TableHead>
                    <TableHead>Kund</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Skickad</TableHead>
                    <TableHead>Förfallodag</TableHead>
                    <TableHead>Påminnelser</TableHead>
                    <TableHead className="text-right">Belopp</TableHead>
                    <TableHead className="text-right">Åtgärder</TableHead>
                  </TableRow>

                </TableHeader>
                <TableBody>
                  {filtered.map((inv) => {
                    const overdue = isOverdue(inv);
                    return (
                      <TableRow key={inv.id} className={overdue ? "bg-rose-50/40" : ""}>
                        <TableCell className="font-mono text-xs">
                          <div>
                            {inv.number || (inv.stripe_invoice_id ? inv.stripe_invoice_id.slice(0, 14) + "…" : "—")}
                          </div>
                          {inv.ocr_reference && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">OCR: {inv.ocr_reference}</div>
                          )}
                          {(inv.billing_reason || inv.period_start) && (
                            <div className="text-[10px] text-muted-foreground mt-0.5 font-sans">
                              {billingReasonLabel(inv.billing_reason)}
                              {inv.period_start ? ` · ${periodLabel(inv.period_start, inv.period_end)}` : ""}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Link to={`/admin/customers/${inv.user_id}`} className="font-medium hover:underline">
                            {customerName(inv.user_id)}
                          </Link>
                          {inv.subscription_id && subNicknames[inv.subscription_id] && (
                            <div className="mt-1">
                              <Badge variant="outline" className="text-[10px] font-normal">
                                {subNicknames[inv.subscription_id]}
                              </Badge>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 flex-wrap">
                            {statusBadge(inv.status)}
                            {overdue && (
                              <Badge className="bg-rose-100 text-rose-900 hover:bg-rose-100">förfallen</Badge>
                            )}
                            {inv.manually_marked_paid && (
                              <span className="text-[10px] text-muted-foreground">(manuell)</span>
                            )}
                            {showStripeStatus(inv) && (
                              <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 text-[10px]">
                                Stripe: {inv.stripe_status}
                              </Badge>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5">
                            {inv.collection_method && (
                              <div>
                                {inv.collection_method === "charge_automatically" ? "Kortdragning" : "Faktura"}
                                {inv.attempt_count ? ` · ${inv.attempt_count} försök` : ""}
                              </div>
                            )}
                            {inv.last_payment_error && (
                              <div className="text-rose-700">{inv.last_payment_error}</div>
                            )}
                            {inv.last_synced_at && (
                              <div>Avstämd {formatDate(inv.last_synced_at)}</div>
                            )}
                          </div>
                        </TableCell>

                        <TableCell className="text-sm text-muted-foreground">{formatDate(inv.sent_at)}</TableCell>
                        <TableCell className="text-sm">
                          <span className={overdue ? "text-rose-700 font-medium" : "text-muted-foreground"}>
                            {formatDate(inv.due_date)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {inv.reminder_count && inv.reminder_count > 0 ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="flex flex-col gap-0.5 text-left">
                                  <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 w-fit">
                                    {inv.reminder_count} skickad{inv.reminder_count === 1 ? "" : "e"}
                                  </Badge>
                                  <span className="text-[10px] text-muted-foreground underline">
                                    senast {formatDate(
                                      inv.reminder_3_sent_at || inv.reminder_2_sent_at || inv.reminder_1_sent_at
                                    )}
                                  </span>
                                  {pausedByInvoice[inv.id] && (
                                    <span className="text-[10px] text-rose-700">prenumeration pausad</span>
                                  )}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="start" className="w-72 text-xs space-y-2">
                                <div className="font-medium text-sm">Skickade påminnelser</div>
                                <div className="text-[11px] text-muted-foreground">
                                  Skickade av Stripe till kundens fakturaadress.
                                </div>
                                <div className="space-y-1">

                                  {[
                                    { n: 1, at: inv.reminder_1_sent_at },
                                    { n: 2, at: inv.reminder_2_sent_at },
                                    { n: 3, at: inv.reminder_3_sent_at },
                                  ].map((r) => (
                                    <div key={r.n} className="flex justify-between gap-2">
                                      <span className="text-muted-foreground">Påminnelse {r.n}</span>
                                      <span className={r.at ? "" : "text-muted-foreground"}>
                                        {r.at
                                          ? new Date(r.at).toLocaleString("sv-SE", {
                                              year: "numeric", month: "short", day: "numeric",
                                              hour: "2-digit", minute: "2-digit",
                                            })
                                          : "—"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                <div className="pt-1 border-t border-border space-y-1">
                                  <div className="text-muted-foreground">
                                    Mottagare:{" "}
                                    <span className="text-foreground break-all">
                                      {profiles.find((p) => p.id === inv.user_id)?.email || "—"}
                                    </span>
                                  </div>
                                  <div className="text-muted-foreground">
                                    Faktura:{" "}
                                    <span className="text-foreground font-mono">{inv.number || "—"}</span>
                                    {" · "}
                                    {formatSEK(inv.amount_cents, inv.currency || "SEK")}
                                  </div>
                                  {inv.subscription_id && (
                                    <div className="text-muted-foreground">
                                      Prenumeration:{" "}
                                      <span className="text-foreground">
                                        {subNicknames[inv.subscription_id] || inv.subscription_id.slice(0, 8)}
                                      </span>
                                    </div>
                                  )}
                                  {pausedByInvoice[inv.id] && (
                                    <div className="text-rose-700">
                                      Prenumerationen pausades på grund av den här fakturan.
                                    </div>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">

                          {formatSEK(inv.amount_cents, inv.currency || "SEK")}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busyId === inv.id}
                              onClick={() => syncOneInvoice(inv.id)}

                              title="Synka denna faktura och dess revisioner från Stripe"
                            >
                              <RefreshCw className={`h-3.5 w-3.5 ${busyId === inv.id ? "animate-spin" : ""}`} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Redigera manuellt (endast hos oss)"
                              onClick={() => setEditInvoice(inv as unknown as Record<string, unknown>)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>

                            {inv.status !== "draft" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busyId === inv.id}
                                title="Ladda ner faktura-PDF direkt (hämtas från Stripe via vår server)"
                                onClick={async () => {
                                  setBusyId(inv.id);
                                  try {
                                    await downloadInvoicePdf(inv.id, inv.number);
                                  } catch (e: any) {
                                    toast.error(e?.message ?? "Kunde inte ladda ner fakturan");
                                  } finally {
                                    setBusyId(null);
                                  }
                                }}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {(inv.hosted_invoice_url || inv.pdf_url) && inv.status !== "draft" && (
                              <Button asChild size="sm" variant="ghost">
                                <a
                                  href={inv.hosted_invoice_url ?? inv.pdf_url ?? "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title="Öppna fakturan hos Stripe"
                                >
                                  <FileText className="h-3.5 w-3.5" />
                                </a>
                              </Button>
                            )}
                            {inv.status === "draft" && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={busyId === inv.id}
                                onClick={() => sendDraft(inv)}
                              >
                                <Send className="h-3.5 w-3.5 mr-1" />
                                Skicka
                              </Button>
                            )}
                            {inv.status !== "paid" && inv.status !== "void" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busyId === inv.id}
                                onClick={() => setConfirmPayId(inv.id)}
                              >
                                Markera betald
                              </Button>
                            )}
                            {(inv.status === "draft" || inv.status === "open" || inv.status === "uncollectible") && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busyId === inv.id}
                                onClick={() => setConfirmVoidId(inv.id)}
                                className="text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                              >
                                Makulera
                              </Button>
                            )}
                            {inv.status === "void" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busyId === inv.id}
                                onClick={() => setResendVoidId(inv.id)}
                                title="Skicka mejl till kunden om att fakturan är makulerad"
                              >
                                <Send className="h-3.5 w-3.5 mr-1" />
                                Makuleringsmejl
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busyId === inv.id}
                              onClick={() => {
                                setCopySource({ id: inv.id, number: inv.number ?? null });
                                setCreateInvoiceOpen(true);
                              }}
                              title="Skapa en ny faktura med samma rader – ändra det du vill"
                            >
                              <Copy className="h-3.5 w-3.5 mr-1" />
                              Kopiera
                            </Button>
                            {(inv.status === "draft" || inv.status === "void") && !inv.paid_at && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busyId === inv.id}
                                onClick={() => setConfirmDeleteId(inv.id)}
                                className="text-muted-foreground hover:text-rose-700 hover:bg-rose-50"
                                title="Ta bort från admin (rör inte Stripe)"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm mark paid */}
      <AlertDialog open={!!confirmPayId} onOpenChange={(open) => !open && setConfirmPayId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Markera som betald?</AlertDialogTitle>
            <AlertDialogDescription>
              Använd det här om kunden betalat utanför Stripe (t.ex. direkt bankgiro). Stripe kommer få
              betalningen registrerad "out of band" och fakturan stängs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmPayId && markPaid(confirmPayId)}>
              Markera betald
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete */}
      <AlertDialog open={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort fakturan från admin?</AlertDialogTitle>
            <AlertDialogDescription>
              Raden tas bort från adminlistan. Stripe-fakturan påverkas inte. Detta går inte att ångra.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDeleteId && deleteInvoice(confirmDeleteId)}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm void */}
      <AlertDialog
        open={!!confirmVoidId}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmVoidId(null);
            setVoidReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Makulera fakturan?</AlertDialogTitle>
            <AlertDialogDescription>
              Fakturan markeras som ogiltig i Stripe och hos kunden. Eventuell kopplad order avbryts
              och rabattkod frigörs. Kunden får automatiskt ett mejl om att fakturan är makulerad och
              inte ska betalas. Detta går inte att ångra.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Anledning (valfritt – visas för kunden i mejlet)
            </label>
            <Input
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="T.ex. Ersätts av en korrigerad faktura"
              maxLength={300}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmVoidId && voidInvoice(confirmVoidId)}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Makulera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resend void notification */}
      <AlertDialog
        open={!!resendVoidId}
        onOpenChange={(open) => {
          if (!open) {
            setResendVoidId(null);
            setVoidReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Skicka makuleringsmejl?</AlertDialogTitle>
            <AlertDialogDescription>
              Kunden får ett mejl om att fakturan är makulerad och inte ska betalas – varken via
              Stripe, OCR eller bankgiro. Använd det här om mejlet inte gick ut när fakturan
              makulerades. Mejlet skickas även om kunden fått det tidigare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Anledning (valfritt – visas för kunden i mejlet)
            </label>
            <Input
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="T.ex. Ersätts av en korrigerad faktura"
              maxLength={300}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => resendVoidId && resendVoidNotice(resendVoidId)}>
              Skicka mejl
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create invoice dialog */}
      <AlertDialog open={!!showCreateFor} onOpenChange={(open) => !open && setShowCreateFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Skapa faktura</AlertDialogTitle>
            <AlertDialogDescription>
              {showCreateFor && (
                <>
                  Kund: <strong>{customerName(showCreateFor.user_id)}</strong>
                  <br />
                  Order: {showCreateFor.product_name} ({formatSEK(showCreateFor.total_cents, showCreateFor.currency || "SEK")})
                  <br />
                  <br />
                  Vill du skicka direkt till kund (30 dagar netto), eller bara skapa ett utkast?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <Button
              variant="outline"
              disabled={!showCreateFor || busyId === showCreateFor.id}
              onClick={() => showCreateFor && createInvoice(showCreateFor.id, false)}
            >
              Spara som utkast
            </Button>
            <AlertDialogAction
              disabled={!showCreateFor || busyId === showCreateFor.id}
              onClick={() => showCreateFor && createInvoice(showCreateFor.id, true)}
            >
              Skapa & skicka
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual edit / manual import */}
      <RecordEditDialog
        open={!!editInvoice || manualInvoiceOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditInvoice(null);
            setManualInvoiceOpen(false);
          }
        }}
        table="invoices"
        title={editInvoice ? "Redigera faktura" : "Lägg till faktura manuellt"}
        fields={editInvoice ? invoiceFields : invoiceCreateFields}
        record={editInvoice}
        onSaved={load}
      />

      <CreateInvoiceSheet
        open={createInvoiceOpen}
        onOpenChange={(o) => {
          setCreateInvoiceOpen(o);
          if (!o) setCopySource(null);
        }}
        sourceInvoiceId={copySource?.id}
        sourceInvoiceNumber={copySource?.number ?? null}
        onCreated={load}
      />
    </AdminLayout>

  );
};

export default AdminInvoicesPage;
